import SliderInput from '@wandb/weave/common/components/elements/SliderInput';
import {
  constFunction,
  constString,
  escapeDots,
  isVoidNode,
  opBooleanAll,
  opIsNone,
  opMap,
  opNumberIsInteger,
  opOr,
  opPick,
} from '@wandb/weave/core';
import {useNodeValue} from '@wandb/weave/react';
import React, {useEffect, useMemo, useRef} from 'react';

import {THEME} from '../../../common/css/color.styles';
import {type PanelStack} from '../availablePanels';
import {PanelComp2} from '../PanelComp';
import {useTableSelection} from './hooks';
import {type additionalProps, type PanelStepperProps} from './types';

export const PanelStepper: React.FC<
  PanelStepperProps & additionalProps
> = props => {
  const {
    safeUpdateConfig,
    config,
    filteredNode,
    outputNode,
    childPanelHandler,
    propertyKeysAndTypes,
  } = props;

  const integralCheckNode = opBooleanAll({
    values: opMap({
      arr: filteredNode,
      mapFn: constFunction({row: filteredNode.type}, ({row}) =>
        opOr({
          lhs: opNumberIsInteger({
            number: opPick({
              obj: row,
              key: constString(escapeDots(config?.workingSliderKey ?? '')),
            }),
          }),
          rhs: opIsNone({
            val: opPick({
              obj: row,
              key: constString(escapeDots(config?.workingSliderKey ?? '')),
            }),
          }),
        })
      ),
    }),
  });

  const {result: integralCheckNodeResult, loading: integralCheckNodeLoading} =
    useNodeValue(integralCheckNode);

  const stepsNode = opPick({
    obj: filteredNode,
    key: constString(escapeDots(config?.workingSliderKey ?? '')),
  });
  const {result: stepsNodeResult, loading: stepsNodeLoading} =
    useNodeValue(stepsNode);

  // Track what steps we've already requested to prevent infinite loops.
  const pendingStepsRef = useRef<number[] | null>(null);
  const pendingCurrentStepRef = useRef<number | null>(null);

  // Reset pending refs when config actually updates (parent processed our request)
  const configSteps = config?.steps;
  const configCurrentStep = config?.currentStep;
  useEffect(() => {
    // If config.steps matches our pending request, clear the pending ref
    if (
      pendingStepsRef.current !== null &&
      configSteps !== undefined &&
      configSteps.length === pendingStepsRef.current.length &&
      configSteps.every((step, i) => step === pendingStepsRef.current![i])
    ) {
      pendingStepsRef.current = null;
    }
    // If config.currentStep matches our pending request, clear the pending ref
    if (
      pendingCurrentStepRef.current !== null &&
      configCurrentStep === pendingCurrentStepRef.current
    ) {
      pendingCurrentStepRef.current = null;
    }
  }, [configSteps, configCurrentStep]);

  useEffect(() => {
    if (
      integralCheckNodeLoading ||
      integralCheckNodeResult == null ||
      !integralCheckNodeResult ||
      stepsNodeLoading ||
      stepsNodeResult == null
    ) {
      return;
    }

    const newSteps: number[] = [...new Set<number>(stepsNodeResult)]
      .filter(n => n != null)
      .sort((a, b) => a - b);

    const currentConfigSteps = config?.steps;

    // Check if steps need to be updated
    const hasStepsChanged =
      currentConfigSteps === undefined ||
      currentConfigSteps.length !== newSteps.length ||
      !currentConfigSteps.every((step, i) => step === newSteps[i]);

    // Check if we've already requested this exact update
    const alreadyRequestedSteps =
      pendingStepsRef.current !== null &&
      pendingStepsRef.current.length === newSteps.length &&
      pendingStepsRef.current.every((step, i) => step === newSteps[i]);

    const configCurrentStep = config?.currentStep;
    const shouldUpdateCurrentStep =
      hasStepsChanged ||
      configCurrentStep === undefined ||
      !newSteps.includes(configCurrentStep);

    const newCurrentStep = newSteps[newSteps.length - 1];

    // Check if we've already requested this exact update
    const alreadyRequestedCurrentStep =
      pendingCurrentStepRef.current === newCurrentStep;

    // Combine updates into a single call to avoid race conditions
    const updates: {steps?: number[]; currentStep?: number} = {};

    if (hasStepsChanged && !alreadyRequestedSteps) {
      pendingStepsRef.current = newSteps;
      updates.steps = newSteps;
    }

    if (shouldUpdateCurrentStep && !alreadyRequestedCurrentStep) {
      pendingCurrentStepRef.current = newCurrentStep;
      updates.currentStep = newCurrentStep;
    }

    if (Object.keys(updates).length > 0) {
      safeUpdateConfig(updates);
    }
  }, [
    integralCheckNodeResult,
    integralCheckNodeLoading,
    stepsNodeResult,
    stepsNodeLoading,
    config,
    safeUpdateConfig,
  ]);

  const tableSelection = useTableSelection(
    propertyKeysAndTypes,
    config,
    safeUpdateConfig
  );

  const configWithTableSelection = useMemo(
    () => ({
      ...config,
      childConfig: {
        ...(config as any)?.childConfig,
        tableSelection,
      },
    }),
    [config, tableSelection]
  );

  const renderPanel = () => {
    if (integralCheckNodeLoading) {
      return null;
    }

    if (!integralCheckNodeResult) {
      return (
        <div
          style={{
            height: '100%',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'light-dark(#666, var(--moon-450))',
            fontSize: '14px',
          }}>
          <div>
            Slider key <i>{config?.workingSliderKey}</i> is not a valid step
            metric. Please use a integral property (e.g. _step).
          </div>
        </div>
      );
    }

    return outputNode != null && !isVoidNode(outputNode) ? (
      <PanelComp2
        input={outputNode}
        inputType={outputNode.type}
        loading={props.loading}
        panelSpec={childPanelHandler as PanelStack}
        configMode={false}
        config={configWithTableSelection}
        context={props.context}
        updateConfig={props.updateConfig}
        updateContext={props.updateContext}
        updateInput={props.updateInput}
      />
    ) : (
      <div
        style={{
          height: '100%',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'light-dark(#666, var(--moon-450))',
          fontSize: '14px',
        }}>
        <div>
          No data found for property <i>{config?.workingKeyAndType?.key}</i>{' '}
          with slider key <i>{config?.workingSliderKey}</i>
        </div>
      </div>
    );
  };

  const renderSlider = () => {
    const shouldShowSlider =
      !integralCheckNodeLoading &&
      integralCheckNodeResult &&
      (config?.steps?.length || 0) > 1;

    return (
      shouldShowSlider && (
        <div
          style={{
            padding: '8px',
            borderTop: '1px solid light-dark(#ddd, var(--moon-750))',
            backgroundColor: THEME.bgPage,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: '8px',
          }}>
          {config?.workingSliderKey}:{' '}
          <SliderInput
            min={config!.steps![0]}
            max={config!.steps![config!.steps!.length - 1]}
            minLabel={config!.steps![0].toString()}
            maxLabel={config!.steps![config!.steps!.length - 1].toString()}
            hasInput={true}
            value={config!.currentStep!}
            step={1}
            ticks={config!.steps}
            onChange={val => {
              safeUpdateConfig({currentStep: val});
            }}
          />
        </div>
      )
    );
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        padding: '2px',
        overflowY: 'auto',
      }}>
      {renderPanel()}
      {renderSlider()}
    </div>
  );
};
